import { ref } from 'vue';
import { useExperienceApi } from '@/shared/api/useExperienceApi';
import type { Experience } from '@/shared/api/experience';
import { useExperienceFavorites } from '@/entities/experience/favorite';

export function useToggleExperienceFavorite() {
  const { addExperienceToFavorites, removeExperienceFromFavorites } = useExperienceApi();
  const favoritesStore = useExperienceFavorites();
  const isPending = ref(false);

  const toggle = async (
    id: Experience['id'],
    onToggle?: (newVal: boolean) => void,
    onError?: (e: unknown) => void,
  ): Promise<boolean> => {
    const wasFavorite = favoritesStore.favoriteIds.has(id);

    // Optimistic UI
    if (wasFavorite) {
      favoritesStore.favoriteIds.delete(id);
      onToggle?.(false);
    } else {
      favoritesStore.favoriteIds.add(id);
      onToggle?.(true);
    }

    isPending.value = true;

    try {
      if (wasFavorite) {
        await removeExperienceFromFavorites({ path: { id } });
      } else {
        await addExperienceToFavorites({ path: { id } });
      }

      return true;
    } catch (error) {
      // Rollback
      if (wasFavorite) {
        favoritesStore.favoriteIds.add(id);
        onToggle?.(true);
      } else {
        favoritesStore.favoriteIds.delete(id);
        onToggle?.(false);
      }
      console.error('Ошибка при переключении избранного:', error);
      onError?.(error);

      return false;
    } finally {
      isPending.value = false;
    }
  };

  return {
    isPending,
    toggle,
  };
}
